import React, { useState } from 'react';
import { 
  Download, 
  FileSpreadsheet, 
  FileText, 
  Printer,
  ChevronDown,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.jsx';
import { Badge } from '../ui/badge.jsx';
import { useToast } from '../../hooks/useToast.js';
import { exportData, formatDataForExport } from '../../utils/exportData.js';
import { debugExportData, flattenExportData } from '../../utils/debugExport.js';

const ExportButton = ({ 
  data = [], 
  filename = 'dados_exportados',
  title = '',
  type = 'generic', // tickets, users, companies, projects, stats, generic
  disabled = false,
  size = 'default',
  variant = 'outline',
  className = '',
  showCount = true,
  customFormatter = null
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const { toast } = useToast();

  // Preparar dados para exportação
  const prepareDataForExport = (format) => {
    try {
      if (!data || data.length === 0) {
        throw new Error('Nenhum dado disponível para exportação');
      }

      debugExportData(data, `Dados originais - tipo: ${type}`);

      // Usar formatter customizado se fornecido
      if (customFormatter && typeof customFormatter === 'function') {
        const formatted = customFormatter(data);
        debugExportData(formatted, 'Após formatter customizado');
        return formatted;
      }

      // Usar formatters predefinidos baseado no tipo
      if (formatDataForExport[type]) {
        const formatted = formatDataForExport[type](data);
        debugExportData(formatted, `Após formatter predefinido (${type})`);
        return formatted;
      }

      // Fallback para dados genéricos - usar função de achatamento melhorada
      console.warn('⚠️ Usando fallback genérico - considere criar formatter específico para este tipo de dados');
      const flattenedData = flattenExportData(data);
      debugExportData(flattenedData, 'Após fallback genérico');
      return flattenedData;
    } catch (error) {
      console.error('❌ Erro ao preparar dados:', error);
      throw error;
    }
  };

  // Função principal de exportação
  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      setExportingFormat(format);

      console.log('📤 Iniciando exportação:', { format, type, records: data?.length });

      // Preparar dados
      const formattedData = prepareDataForExport(format);
      
      // Configurar opções de exportação
      const exportOptions = {
        format,
        filename,
        title: title || `Relatório ${type}`,
      };

      // Executar exportação
      await exportData(formattedData, exportOptions);

      // Feedback de sucesso
      toast({
        title: "✅ Exportação Concluída",
        description: `${formattedData.length} registros exportados em formato ${format.toUpperCase()}`,
      });

      console.log('✅ Exportação concluída com sucesso');

    } catch (error) {
      console.error('❌ Erro na exportação:', error);
      
      toast({
        title: "❌ Erro na Exportação",
        description: error.message || 'Erro desconhecido durante a exportação',
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
    }
  };

  // Verificar se há dados para exportar
  const hasData = data && data.length > 0;
  const isDisabled = disabled || !hasData || isExporting;

  // Opções de exportação
  const exportOptions = [
    {
      key: 'csv',
      label: 'CSV',
      description: 'Planilha compatível com Excel',
      icon: FileText,
      color: 'text-green-600'
    },
    {
      key: 'excel',
      label: 'Excel',
      description: 'Arquivo do Microsoft Excel',
      icon: FileSpreadsheet,
      color: 'text-green-700'
    },
    {
      key: 'pdf',
      label: 'PDF',
      description: 'Documento PDF para impressão',
      icon: Printer,
      color: 'text-red-600'
    }
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={isDisabled}
          className={`${className} ${isExporting ? 'cursor-not-allowed' : ''}`}
        >
          {isExporting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Exportando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Exportar
              {showCount && hasData && (
                <Badge variant="secondary" className="ml-2">
                  {data.length}
                </Badge>
              )}
              <ChevronDown className="w-3 h-3 ml-1" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Exportar Dados</span>
          {hasData && (
            <Badge variant="outline">
              {data.length} registro{data.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />

        {!hasData ? (
          <DropdownMenuItem disabled>
            <FileText className="w-4 h-4 mr-2 text-gray-400" />
            <div>
              <div className="font-medium text-gray-500">Nenhum dado disponível</div>
              <div className="text-xs text-gray-400">Adicione dados para exportar</div>
            </div>
          </DropdownMenuItem>
        ) : (
          exportOptions.map((option) => {
            const IconComponent = option.icon;
            const isCurrentlyExporting = exportingFormat === option.key;
            
            return (
              <DropdownMenuItem
                key={option.key}
                onClick={() => handleExport(option.key)}
                disabled={isExporting}
                className={`cursor-pointer ${isCurrentlyExporting ? 'bg-blue-50' : ''}`}
              >
                {isCurrentlyExporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin text-blue-600" />
                ) : (
                  <IconComponent className={`w-4 h-4 mr-2 ${option.color}`} />
                )}
                <div>
                  <div className="font-medium">
                    {option.label}
                    {isCurrentlyExporting && (
                      <span className="text-blue-600 ml-1">
                        - Exportando...
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{option.description}</div>
                </div>
              </DropdownMenuItem>
            );
          })
        )}

        {hasData && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-xs text-gray-500">
              💡 PDF abrirá nova janela para impressão
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton; 